import {
  Box,
  Button,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Portal,
  Snackbar,
  Theme,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ReactElement, useEffect, useState } from 'react';
import { ArrowLeft, XCircle } from 'react-feather';

// design system
import { colors } from 'design-system';

// data-layer-cr
import { NATURAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import { validLegalEntity } from 'data-layer-cr/dist/functions/quotations';
import { Quotation } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

import { connect, ConnectedProps } from 'react-redux';
import { cleanCurrentQuotationReference } from 'src/store/quotation/actions';
import { RootState } from 'src/store/rootReducer';

// subcomponents
import { faHexagonXmark, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NetworkIndicator } from 'src/components/General';
import { REQUESTING_FETCH, SUCCESS_FETCH } from 'src/constants/fetchStatus';
import { ERROR_EMISSION } from 'src/constants/quotationStatus';
import { useGetLatestStatusError } from 'src/hooks/useGetLatestStatusError';
import ContactClient from '../../../ContactClient';
import { InspectionSearch, QuotationHistoryContent } from './pages';

// redux related
import {
  GET_DETAILS,
  GET_DETAILS_CHANGES,
} from '../../../../store/quotation/requestIdentifiers';
import { setQuotationHistoryStepper } from '../../../../store/steppers/actions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 444,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      display: 'flex',
      flexFlow: 'column',
    },
    appBar: {
      minHeight: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.madison,
      color: colors.white,
      position: 'relative',
    },
    appBarContent: {
      marginTop: theme.spacing(1),
    },
    backArrow: {
      position: 'absolute',
      top: theme.spacing(2),
      left: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
    },
    whiteText: {
      color: colors.white,
    },
    toolbar: {
      marginBottom: 20,
    },
    notificationToastButton: {
      color: colors.bright_turquoise,
    },
    notificationSnackBar: {
      backgroundColor: '#0D2041',
      color: colors.white,
      borderRadius: 10,
      fontWeight: 'lighter',
      width: 346,
      height: 65,
      marginRight: 30,
    },
    alertErrorCard: {
      padding: '1rem',
      margin: '1rem 1rem 1rem 3.5rem',
      borderRadius: 10,
      display: 'flex',
      gap: '0.5rem',
    },
    alertErrorCardBackground: {
      backgroundColor: '#FFDCE0',
    },
    alertErrorCardContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      flex: '1',
    },
    alertErrorCardTextColor: {
      color: '#910314',
    },
    alertErrorCardCloseIcon: {
      cursor: 'pointer',
    },
  }),
);

export function QuotationHistoryLayout({
  quotationHash,
  quotation,
  cleanCurrentQuotationReferenceAction,
  currentStep,
  onCloseClicked,
  setHistoryStep,
}: Props): ReactElement {
  const classes = useStyles();
  const [confirmation, setConfirmation] = useState(false);
  const [closeError, setCloseError] = useState(false);
  const { data, fetchStatus } = useGetLatestStatusError(quotation);
  const requestStatus = useNetworkObserver(GET_DETAILS);
  const changeRequestStatus = useNetworkObserver(GET_DETAILS_CHANGES);

  // reset to main page on unMount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => (): void => setHistoryStep(0), []);

  const pages = [
    <QuotationHistoryContent
      quotationHash={quotationHash}
      data-testid="content-page"
    />,
    <InspectionSearch data-testid="inspection-page" />,
  ];

  const fullName =
    quotation.customerType === NATURAL_PERSON
      ? `${quotation.customer.firstName} ${quotation.customer.lastName}`
      : validLegalEntity(quotation).name;

  /**
   * Opening this screen can briefly show the previous quotation's information before
   * the loading method is called. To prevent this, always clean the current quotation's
   * reference when exitting this screen.
   */
  function requestClose(): void {
    cleanCurrentQuotationReferenceAction();
    onCloseClicked();
  }

  return (
    <div className={classes.root}>
      <nav className={classes.appBar}>
        {requestStatus !== 'requesting' &&
          changeRequestStatus !== 'requesting' && (
            <>
              <Toolbar data-testid="toolbar" className={classes.toolbar}>
                <Grid container spacing={1} className={classes.appBarContent}>
                  <Grid item xs={12} container justifyContent="center">
                    <Typography
                      data-testid="fullname"
                      variant="body2"
                      className={classes.whiteText}
                    >
                      {fullName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} container justifyContent="center">
                    <Typography
                      data-testid="brand-model"
                      variant="h4"
                      className={classes.whiteText}
                    >
                      {quotation.summary.carBrand} {quotation.summary.carModel}{' '}
                      / {quotation.summary.carYear}
                    </Typography>
                  </Grid>
                </Grid>
              </Toolbar>
            </>
          )}
        {currentStep !== 0 && (
          <IconButton
            data-testid="back-button"
            className={classes.backArrow}
            onClick={() => setHistoryStep(0)}
          >
            <ArrowLeft color={colors.bright_turquoise} />
          </IconButton>
        )}
        <IconButton
          id="sales-web-button-quotationhistoryclose"
          data-testid="close-button"
          className={classes.closeButton}
          onClick={requestClose}
        >
          <XCircle strokeWidth="2px" color="#fff" />
        </IconButton>
      </nav>
      {currentStep === 0 && (
        <ContactClient
          data-testid="contact-client"
          quotation={quotation}
          showNotification={setConfirmation}
        />
      )}
      <Portal>
        <Snackbar
          open={confirmation}
          data-testid="portal"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <Alert
            data-testid="alert-succesful-notification"
            variant="filled"
            icon={false}
            className={classes.notificationSnackBar}
            action={
              <Button
                size="small"
                data-testid="alert-succesful-button"
                id="sales-web-close-notification-button"
                className={classes.notificationToastButton}
                onClick={(): void => {
                  setConfirmation(false);
                }}
              >
                ENTENDIDO
              </Button>
            }
          >
            La cotización ha sido enviada por correo al cliente.
          </Alert>
        </Snackbar>
      </Portal>
      {quotation.status.id === ERROR_EMISSION &&
        !closeError &&
        (data && fetchStatus === SUCCESS_FETCH ? (
          <Box
            className={`${classes.alertErrorCard} ${classes.alertErrorCardBackground}`}
          >
            <FontAwesomeIcon icon={faHexagonXmark} size="2xl" color="#BB0016" />
            <Box className={classes.alertErrorCardContent}>
              <Typography
                variant="h4"
                className={classes.alertErrorCardTextColor}
              >
                <strong>Error</strong>
                <br />
                {data.errorTitle}
              </Typography>
              <Typography
                variant="h4"
                className={classes.alertErrorCardTextColor}
              >
                <strong>Mensaje:</strong> {data.errorMessage}
              </Typography>
            </Box>
            <FontAwesomeIcon
              data-testid="close-error-card"
              icon={faXmark}
              size="2xl"
              color="#BB0016"
              className={classes.alertErrorCardCloseIcon}
              onClick={(): void => {
                setCloseError(true);
              }}
            />
          </Box>
        ) : (
          <Box className={classes.alertErrorCard}>
            <NetworkIndicator
              data-testid="network-indicator"
              requesting={fetchStatus === REQUESTING_FETCH}
              message="Obteniendo información del error"
            />
          </Box>
        ))}
      {/* wrap pages to avoid index out of bounds */}
      {pages[currentStep % pages.length]}
    </div>
  );
}

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  currentStep: number;
} => ({
  quotation: state.quotation.currentQuotation,
  currentStep: state.stepper.quotationHistoryPage,
});

const reduxActions = {
  cleanCurrentQuotationReferenceAction: cleanCurrentQuotationReference,
  setHistoryStep: setQuotationHistoryStepper,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  quotationHash: string;
  onCloseClicked: () => void;
}

export default connector(QuotationHistoryLayout);
